/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import './style/dvrdGroupedSelect.scss';
import React, {ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {ErrorType, GroupedSelectItem, SelectValueType} from "../util/interfaces";
import classNames from 'classnames';
import AwesomeIcon from "../icon/awesomeIcon";
import defer from 'lodash.defer';
import {stopPropagation} from "../util/controlUtil";


interface Props {
    onChange: (value: SelectValueType, evt?: React.MouseEvent | React.ChangeEvent) => void;
    items: Array<GroupedSelectItem>;
    value: SelectValueType;
    label?: string;
    disabled?: boolean;
    error?: ErrorType;
    className?: string;
    labelClassName?: string;
    valueClassName?: string;
    itemContainerClassName?: string;
    itemClassName?: string;
    errorClassName?: string;
    itemsPosition?: 'top' | 'bottom';
    maxItemsHeight?: number | string;
    highlightSelected?: true;
    placeholder?: string;
    searchable?: boolean;
}

export type GroupedSelectRef = { open: VoidFunction; close: VoidFunction; toggle: (forcedValue?: boolean) => void };

function findInItems(items: Array<GroupedSelectItem>, value: string | number): GroupedSelectItem | null {
    for (const item of items) {
        if (item.value === value) return item;
        if (item.children) {
            const childItem = findInItems(item.children, value);
            if (childItem) return childItem;
        }
    }
    return null;
}

function itemMatchesSearch(item: GroupedSelectItem, search: string): boolean {
    if (!search) return true;
    return item.label.toString().toLowerCase().includes(search.toLowerCase());
}

function DVRDGroupedSelect(props: Props, ref: ForwardedRef<GroupedSelectRef>) {
    const {
        className, label, labelClassName, value, placeholder, valueClassName, error, itemContainerClassName,
        onChange, items, itemClassName, disabled, errorClassName, highlightSelected, searchable
    } = props;
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [searchActive, setSearchActive] = useState(false);
    const maxItemsHeight: string | undefined = useMemo(() => {
        const {maxItemsHeight} = props;
        if (maxItemsHeight) {
            if (typeof maxItemsHeight === 'string') return maxItemsHeight;
            return maxItemsHeight + 'px';
        }
        return undefined;
    }, [props.maxItemsHeight]);
    const itemsPosition = useMemo(() => {
        return props.itemsPosition ?? 'bottom';
    }, [props.itemsPosition]);
    const selectedLabel: string | number | React.ReactElement | null = useMemo(() => {
        const selectedItem = findInItems(items, value);
        if (!selectedItem) return null;
        return selectedItem.valueLabel ?? selectedItem.label ?? '';
    }, [value, items]);
    const longestValue = useMemo(() => {
        let longest: string = '';
        for (const item of items)
            if (item.label.toString().length > longest.length)
                longest = item.label.toString();
        return longest;
    }, [items]);
    const availableItems = useMemo(() => {
        if (!searchable || !search.trim()) return items;
        const _items: Array<GroupedSelectItem> = [];
        for (const item of items) {
            if (itemMatchesSearch(item, search)) _items.push(item);
            else if (item.children) {
                if (!!item.children.find((child: GroupedSelectItem) => itemMatchesSearch(child, search)))
                    _items.push(item);
            }
        }
        return _items;
    }, [items, searchable, search]);
    const itemsContainer = useRef<HTMLDivElement>(null);

    function onChangeSearch(evt: React.ChangeEvent<HTMLInputElement>) {
        const {value} = evt.target;
        setSearch(value);
    }

    function onFocusSearch() {
        if (searchable) {
            setSearchActive(true);
            setOpen(true);
        }
    }

    function onBlurSearch() {
        if (searchable) setSearchActive(false);
    }

    function onSelectItem(item: GroupedSelectItem) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            if (item.value !== undefined) {
                onChange(item.value, evt);
                onToggleOpen(false);
            }
        }
    }

    function onToggleOpen(forcedValue?: boolean) {
        if (disabled) forcedValue = false;
        setOpen(forcedValue ?? !open);
    }

    function onClickRoot(evt: React.MouseEvent) {
        evt.stopPropagation();
        onToggleOpen();
    }

    function onOpen() {
        onToggleOpen(true);
    }

    function onClose() {
        onToggleOpen(false);
    }

    function clickListener() {
        setOpen(false);
    }

    function scrollSelectedInView() {
        itemsContainer.current?.scrollTo({top: 0});
        if (!highlightSelected || selectedLabel === null) return;
        const selectedItem = findInItems(items, value);
        if (!selectedItem || !selectedItem.value) return;
        const itemID = `dvrd-grouped-item-${selectedItem.value}`;
        const element = document.getElementById(itemID);
        if (!element) return;
        element.scrollIntoView({behavior: 'smooth'});
    }

    function renderLabel() {
        if (!label) return null;
        return <label className={classNames('grouped-select-label', labelClassName)}>{label}</label>;
    }

    function renderValue() {
        const chevIcon = itemsPosition === 'bottom' ? 'chevron-down' : 'chevron-up';
        return (
            <div className={classNames('grouped-select-value-container', searchable && 'searchable', valueClassName)}>
                {renderValueInput()}
                {renderValueCustom()}
                {renderValueLabel()}
                {renderValuePlaceholder()}
                <AwesomeIcon name={chevIcon} className='chev-icon'/>
                <div style={{height: 0, visibility: 'hidden'}}>{longestValue}</div>
            </div>
        );
    }

    function renderValueInput() {
        if (!searchable) return null;
        const _value = searchActive ? search : selectedLabel?.toString();
        return (
            <input onChange={onChangeSearch} value={_value} onFocus={onFocusSearch}
                   onBlur={onBlurSearch} className='dvrd-grouped-select-search' disabled={disabled}
                   onClick={stopPropagation} placeholder={placeholder}/>
        )
    }

    function renderValueCustom() {
        if (selectedLabel === null || searchable) return null; // Nothing selected, or using a search input
        if (['string', 'number'].includes(typeof selectedLabel)) return null; // Not a custom element
        const element = selectedLabel as React.ReactElement<any>;
        return React.cloneElement(element, {
            ...element.props,
            className: classNames(element.props?.className, 'grouped-select-value')
        });
    }

    function renderValueLabel() {
        if (selectedLabel === null || searchable) return null; // Nothing selected, or using a search input
        if (!['string', 'number'].includes(typeof selectedLabel)) return null; // A custom element
        return (
            <label className='grouped-select-value'>{selectedLabel}</label>
        );
    }

    function renderValuePlaceholder() {
        if (selectedLabel !== null || searchable) return null; // Something is selected, or we are using a search input
        const placeholderHidden = !placeholder?.length;
        return (
            <label className={classNames('grouped-select-placeholder', placeholderHidden && 'hidden')}>
                {placeholderHidden ? 'placeholder' : placeholder}</label>
        );
    }

    function renderItemsContainer() {
        return (
            <div className={classNames('grouped-select-items', itemContainerClassName)}
                 style={{maxHeight: maxItemsHeight}} ref={itemsContainer}>
                {availableItems.map(renderItem())}
            </div>
        )
    }

    function renderItem(layer: number = 0) {
        return function (item: GroupedSelectItem, index: number) {
            if (item.selectable !== undefined) return null;
            const isGroupHead = !!item.children?.length;
            const isElement = !['string', 'number'].includes(typeof item.label);
            const padding = `${layer + .5}rem`;
            const isSelectable = item.value !== undefined;
            const isSelected = item.value === value;
            return (
                <div id={`dvrd-grouped-item-${item.value}`} key={index}
                     className={classNames('dvrd-grouped-item', itemClassName)}
                     onClick={onSelectItem(item)}>
                    {isElement ? renderCustomLabel(item.label as React.ReactElement, padding, isGroupHead,
                            isSelectable, isSelected) :
                        <label className={classNames('grouped-item-label', isGroupHead && 'group-head',
                            isSelectable && 'selectable', (isSelected && highlightSelected) && 'selected')}
                               style={{paddingLeft: padding}}>{item.label}</label>}
                    {!!item.children && (
                        <div className='dvrd-grouped-item-children'>
                            {item.children.map(renderItem(layer + 1))}
                        </div>
                    )}
                </div>
            )
        }
    }

    function renderCustomLabel(label: React.ReactElement<any>, padding: string, isGroupHead: boolean, isSelectable: boolean,
                               isSelected: boolean) {
        const style = label.props?.style ?? {};
        return React.cloneElement(label, {
            ...label.props,
            style: {...style, paddingLeft: padding},
            className: classNames('grouped-item-label', isGroupHead && 'group-head', isSelectable && 'selectable',
                (isSelected && highlightSelected && 'selected'),
                label.props?.className)
        });
    }

    function renderError() {
        if (!error) return null;
        return (
            <label className={classNames('dvrd-grouped-select-error', errorClassName)}>{error}</label>
        );
    }

    useEffect(() => {
        if (open) {
            document.body.addEventListener('click', clickListener);
            defer(scrollSelectedInView);
        } else document.body.removeEventListener('click', clickListener);
    }, [open]);

    useImperativeHandle(ref, () => ({
        open: onOpen, close: onClose, toggle: onToggleOpen
    }));

    return (
        <div className={classNames('dvrd-grouped-select', !!error && 'error', disabled && 'disabled', itemsPosition,
            (open && !disabled) && 'open', className)}
             onClick={onClickRoot}>
            {renderLabel()}
            <div className='dvrd-grouped-select-content'>
                {renderValue()}
                {renderItemsContainer()}
            </div>
            {renderError()}
        </div>
    )
}

export default forwardRef<GroupedSelectRef, Props>(DVRDGroupedSelect);